// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import {
  MatDialog
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import {
  finalize,
  take
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DeadlinesService } from "src/app/modules/deadlines/deadlines.service";
import { PricesService } from "src/app/modules/prices/prices.service";
import { openDocumentArchiveDossierDialog } from "src/app/modules/properties/components/document-archive-dossier-dialog/document-archive-dossier-dialog.component";
import { openDocumentArchiveExportContractsDialog } from "src/app/modules/properties/components/document-archive-export-contracts-dialog/document-archive-export-contracts-dialog.component";
import { openDocumentArchiveExportRasDialog } from "src/app/modules/properties/components/document-archive-export-ras-dialog/document-archive-export-ras-dialog.component";
import { ExportCdDocsDialogComponent } from "src/app/modules/properties/components/export-cd-docs-dialog/export-cd-docs-dialog.component";
import { ExportContractsDialogComponent } from 'src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component';
import { ExportRasDocsDialogComponent } from 'src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component';
import { PrivacyDocsListDialogComponent } from 'src/app/modules/properties/components/privacy-docs-list-dialog/privacy-docs-list-dialog.component';
import { CondoStructuresService } from "src/app/modules/properties/condo-structures.service";
import {
  guestArchiveLink,
  guestSearchLink,
  showsGuestSearch,
  showsStaffInsert,
  showsStaffSearch,
} from "src/app/modules/properties/document-archive-entries.utils";
import { DocumentArchiveExportDownloadService } from "src/app/modules/properties/document-archive-export-download.service";
import {
  DocumentArchiveExportMenu,
  DocumentArchiveExportTarget,
  documentArchiveExportTargetOf,
  legacyPageExportMenu,
} from "src/app/modules/properties/document-archive-export.utils";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { resolveMappingInfo } from "src/app/modules/properties/property-mapping-info.utils";
import { PropertyRemappingsService } from "src/app/modules/properties/property-remappings.service";
import { StatementsService } from "src/app/modules/properties/statements.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { UsersService } from "src/app/modules/users/users.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { Company } from "src/app/shared/models/company/company.model";
import { PropertyRemappingItem } from "src/app/shared/models/property/property-remapping.interface";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import {
  DATA_PROCESSOR_APPOINTMENT_TEMPLATE,
  DEFAULT_PRIVACY_DOC_EXTENSION,
  PRIVACY_DOC_MIME_EXTENSIONS,
  PrivacyDocsService
} from "src/app/shared/services/privacy-docs.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";

@Component({
  selector: "app-property-home",
  templateUrl: "./property-home.page.html",
  styleUrls: ["./property-home.page.scss"],
})
export class PropertyHomeComponent implements OnInit, AfterViewInit, OnDestroy {
  // Google Map
  // @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  // @ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;
  mapZoom = 18;
  mapCenter: google.maps.LatLngLiteral;
  mapOptions: google.maps.MapOptions = {
    zoomControl: true,
    scrollwheel: false,
    disableDoubleClickZoom: true,
    mapTypeId: "hybrid",
  };
  markers = [];
  // End - Google Map

  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  detailCardIsLoading = true;

  loggedUser: IUser;
  loggedUserPermissions: string[];
  currentRole: any;
  loggedUserShared = false;
  loggedUserTemporary = false;
  loggedUserMaster = false;
  loggedUserAdmin = false;
  loggedUserRoleType = "";

  subscriptionDocumentUpload = false;
  propertyPrivacyActive = false;
  propertyPrivacySagActive = false;
  masterOnlySuppliersAndNfcTag = false;
  masterOnlySuppliers = false;
  masterOnlyNfcTag = false;

  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = "";
  addressString = "";
  mappatore = "";
  lastMappingEditDate: any = null;
  // Most recent row of the "Elenco rimappature" table, second source of
  // "Mappatore" and "Ultima modifica alla mappatura" (see resolveMappingInfo)
  latestRemapping: PropertyRemappingItem | null = null;
  latestRemappingLoaded = false;
  // True once both the property and the latest remapping are known, so the
  // mapper row never flashes "Non presente" before the remapping arrives
  mappingInfoReady = false;

  companyData: ICompany;
  company: Company = new Company();

  previousSubscription: any = {};
  subscriptionType = "FREE";

  mappaturaOpen = false;

  coverImage = "/assets/img/property-sample.png";

  dataProcessorAppointmentDownloading = false;

  masterAndSubscriptionPrivacy = false;

  /**
   * `subscription.key` of the property for the rules of the «SCARICA» column once the switch is live:
   * `""` for a property with no plan, which the legacy treats as a paid one, `null` until the property
   * has answered. Kept apart from `subscriptionType`, which reads "FREE" in both cases.
   */
  exportPlanKey: string | null = null;

  /** The Excel being downloaded from the new archive, `null` when none (@see exportFromArchiveV3) */
  exportingTarget: DocumentArchiveExportTarget | null = null;

  /** The download of that Excel, cancelled when the page closes */
  private exportSubscription: Subscription | null = null;

  /**
   * True once the switch puts the new archive live: the guest buttons then lead to its pages, and the
   * staff's «Ricerca documenti» and «Inserisci nuovo documento» follow its rules
   */
  readonly archiveV3Live = isArchiveV3Live();

  /**
   * «Archivio fascicolo documentale» of the guests: the legacy dossier until the switch, the new one
   * after it.
   *
   * @since 3.0.0
   */
  get guestArchiveRoute(): string | string[] {
    return guestArchiveLink(this.archiveV3Live, this.lang, this.propertyId);
  }

  /**
   * «Ricerca documenti» of the guests: the legacy search until the switch, the new one after it.
   *
   * @since 3.0.0
   */
  get guestSearchRoute(): string | string[] {
    return guestSearchLink(this.archiveV3Live, this.lang, this.propertyId);
  }

  /**
   * Whether the guests see «Ricerca documenti»: always until the switch; after it, only with the
   * permissions of the new search, which temporary roles never hold.
   *
   * @since 3.0.0
   */
  get guestSearchShown(): boolean {
    return showsGuestSearch(this.archiveV3Live, this.loggedUserPermissions);
  }

  /**
   * Whether the staff sees «Ricerca documenti»: always until the switch; after it, only with the
   * permissions of the new search.
   *
   * @since 3.0.0
   */
  get staffSearchShown(): boolean {
    return showsStaffSearch(this.archiveV3Live, this.loggedUserPermissions);
  }

  /**
   * Whether the staff sees «Inserisci nuovo documento»: always until the switch; after it, with the
   * rule of the bar of the new pages.
   *
   * @since 3.0.0
   */
  get staffInsertShown(): boolean {
    return showsStaffInsert(this.archiveV3Live, this.loggedUserRoleType, this.loggedUserPermissions);
  }

  /**
   * The entries of the «SCARICA» column as far as the switch decides them: every one, under the rules
   * of the template, until the switch; once it is live, those of the «Esporta» menu of the new dossier,
   * whose routes they then call (@see legacyPageExportMenu).
   *
   * @since 3.0.0
   */
  get downloadMenu(): DocumentArchiveExportMenu {
    return legacyPageExportMenu(this.archiveV3Live, {
      roleType: this.loggedUserRoleType,
      permissions: this.loggedUserPermissions,
      subscriptionKey: this.exportPlanKey,
    });
  }

  /**
   * Whether the «SCARICA» title is shown: to the staff, as long as the column has at least one entry.
   * Until the switch its first three entries are always there; once it is live, a role may get none.
   *
   * @since 3.0.0
   */
  get downloadColumnShown(): boolean {
    if (this.loggedUserTemporary || this.loggedUserShared) {
      return false;
    }
    const menu = this.downloadMenu;
    return (
      menu.inserted ||
      menu.missing ||
      menu.ras ||
      (this.loggedUserAdmin && menu.contracts) ||
      (this.loggedUserAdmin && this.propertyPrivacyActive) ||
      (this.loggedUserMaster && this.propertyPrivacySagActive)
    );
  }

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    public cancelContractDialog: MatDialog,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private subscriptionsService: SubscriptionsService,
    private pricesService: PricesService,
    private balancesService: BalancesService,
    private listsService: ListsService,
    private userService: UsersService,
    private deadlinesService: DeadlinesService,
    private suppliersService: SuppliersService,
    private settingsService: SettingsService,
    private documentArchivesService: DocumentArchivesService,
    private condoStructuresService: CondoStructuresService,
    private statementsService: StatementsService,
    private privacyDocsService: PrivacyDocsService,
    private propertyRemappingsService: PropertyRemappingsService,
    private documentArchiveExportDownloadService: DocumentArchiveExportDownloadService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.propertyId = this.activatedRoute.snapshot.params.propertyId;

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        this.loggedUserRoleType = this.currentRole.role.type;

        if (this.currentRole.role.type === "shared") {
          this.loggedUserShared = true;
        }
        if (this.currentRole.role.type === "temporary") {
          this.loggedUserTemporary = true;
        }

        if (
          this.currentRole.role.type === "master" ||
          this.currentRole.role.type === "collaborator"
        ) {
          this.loggedUserMaster = true;
          if (this.currentRole.company._id !== "") {
            this.subscriptions.push(
              this.companiesService
                .get(this.currentRole.company._id)
                .subscribe((res) => {
                  if (res.status) {
                    this.companyData = res.data;
                    this.company.setData(res.data);
                    if (
                      !res.data.subscription.documentUpload  && 
                      res.data.subscription.nfcAccess &&
                      res.data.subscription.suppliers
                    ) {
                      this.masterOnlySuppliersAndNfcTag = true;
                    } else if (
                      !res.data.subscription.documentUpload  && 
                      !res.data.subscription.nfcAccess &&
                      res.data.subscription.suppliers
                    ) {
                      this.masterOnlySuppliers = true;
                    } else if (
                      !res.data.subscription.documentUpload  && 
                      res.data.subscription.nfcAccess &&
                      !res.data.subscription.suppliers
                    ) {
                      this.masterOnlyNfcTag = true;
                    }
                  }
                })
            );
          }
        }

        if (
          this.currentRole.role.type === "admin" ||
          this.currentRole.role.type === "operator"
        ) {
          this.loggedUserAdmin = true;
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get requested property data
    this.subscriptions.push(
      this.propertiesService.get(this.propertyId).subscribe(
        (res) => {
          if (res.status) {
            // Set permission data
            this.property.setData(res.data);
            this.propertyData = res.data;

            // "Mappatore" and "Ultima modifica alla mappatura": computed once
            // the latest remapping is known too (whichever response comes last)
            this.updateMappingInfo();

            const countryCode = this.propertyData.address.countryCodeTwo
              ? this.propertyData.address.countryCodeTwo
              : this.propertyData.address.countryCode;
            this.addressString = `${this.propertyData.address.route} ${this.propertyData.address.streetNumber},`;
            this.addressString = `${this.addressString} ${this.propertyData.address.postalCode} ${this.propertyData.address.city}, ${countryCode}`;

            for (const prop in res.data.subscription) {
              this.previousSubscription[prop] = res.data.subscription[prop];
            }

            // The plan as the rules of the exports read it once the switch is live
            this.exportPlanKey =
              this.propertyData.subscription && typeof this.propertyData.subscription.key === "string"
                ? this.propertyData.subscription.key
                : "";

            if (
              this.propertyData.subscription &&
              this.propertyData.subscription.key
            ) {
              this.subscriptionType = this.propertyData.subscription.key;
              if (this.propertyData.subscription.documentUpload) {
                this.subscriptionDocumentUpload = true;
              }

              if (this.subscriptionType === 'PRIVACY' && this.loggedUserMaster) {
                this.masterAndSubscriptionPrivacy = true;
              }
            }

            this.propertyPrivacyActive = !!(
              this.propertyData.subscription &&
              (this.propertyData.subscription.privacy || this.propertyData.subscription.privacySag)
            );

            this.propertyPrivacySagActive = !!(
              this.propertyData.subscription &&
              this.propertyData.subscription.privacySag
            );

            if (this.propertyData.structure) {
              this.subscriptions.push(
                this.condoStructuresService
                  .get(this.propertyData.structure)
                  .subscribe(
                    (res) => {
                      if (res.status && res.data) {
                        if (
                          res.data.anagrafica &&
                          res.data.anagrafica.files &&
                          Array.isArray(res.data.anagrafica.files) &&
                          res.data.anagrafica.files.length
                        ) {
                          this.coverImage = `${this.configService.apiHost}/${res.data.anagrafica.files[0].path}`;
                        } else {
                          this.coverImage = "/assets/img/property-sample.png";
                        }
                        this.mappaturaOpen = res.data.open;
                      }
                    },
                    (err) => {
                      this.helperService.handleError(err);
                    }
                  )
              );
            }

            this.detailCardIsLoading = false;
            return;
          }

          // Something bad happened
          // redirect user to home
          this.detailCardIsLoading = false;
        },
        (err) => {
          this.detailCardIsLoading = false;
        }
      )
    );

    // Second source of "Mappatore" and "Ultima modifica alla mappatura",
    // requested in parallel with the property
    this.loadLatestRemapping();

    this.markers.push({
      position: {
        lat: 45.4617824,
        lng: 9.1219734,
      },
      label: {
        color: "red",
        text: "Marker label " + (this.markers.length + 1),
      },
      title: "Marker title " + (this.markers.length + 1),
      info: "Marker info " + (this.markers.length + 1),
      options: {
        animation: google.maps.Animation.BOUNCE,
      },
    });
  }

  /**
   * Loads the most recent remapping of the property: the first row of the
   * "Elenco rimappature" table (property-status page) with the table default
   * sort, date desc.
   *
   * The back-end list requires `canReadCondoStructuresList`, as for the table:
   * without it the request is not sent. When the request is not sent, returns
   * no rows or fails (the error is reported as in the rest of the page), the
   * next sources of resolveMappingInfo are used.
   *
   * @since 3.0.0
   */
  loadLatestRemapping(): void {
    if (
      !Array.isArray(this.loggedUserPermissions) ||
      !this.loggedUserPermissions.includes("canReadCondoStructuresList")
    ) {
      this.onLatestRemappingLoaded(null);
      return;
    }

    this.subscriptions.push(
      this.propertyRemappingsService
        .getList(this.propertyId, 0, 1, "date", "desc")
        .subscribe(
          (res) => {
            const items =
              res && res.status && res.data && Array.isArray(res.data.items)
                ? res.data.items
                : [];
            this.onLatestRemappingLoaded(items.length > 0 ? items[0] : null);
          },
          (err) => {
            this.helperService.handleError(err);
            this.onLatestRemappingLoaded(null);
          }
        )
    );
  }

  /**
   * Stores the outcome of loadLatestRemapping and refreshes the mapping info.
   *
   * @since 3.0.0
   *
   * @param remapping Most recent remapping, or null when there is none or it could not be read
   */
  onLatestRemappingLoaded(remapping: PropertyRemappingItem | null): void {
    this.latestRemapping = remapping;
    this.latestRemappingLoaded = true;
    this.updateMappingInfo();
  }

  /**
   * Sets "Mappatore" and "Ultima modifica alla mappatura" once both the
   * property and the latest remapping are known. Called by both responses,
   * which can arrive in any order: the first one to arrive is a no-op.
   *
   * @since 3.0.0
   */
  updateMappingInfo(): void {
    if (!this.propertyData || !this.latestRemappingLoaded) {
      return;
    }
    const mappingInfo = resolveMappingInfo(
      this.propertyData.propertyStatus,
      this.latestRemapping
    );
    this.mappatore = mappingInfo.mapper;
    this.lastMappingEditDate = mappingInfo.lastEditDate;
    this.mappingInfoReady = true;
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // console.log("ngAfterViewInit");
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // console.log("ngOnDestroy");

    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
  }

  /**
   * CD Export file
   * export the files of all condominium documents in zip format
   *
   * @since 1.0.0
   */
  onExportFile(): void {
    // With the 3.0.0 archive switched on the dossier comes from it, part by part (NEW-M17)
    if (this.archiveV3Live) {
      openDocumentArchiveDossierDialog(this.dialog, this.propertyId);
      return;
    }
    const dialogRef = this.dialog.open(ExportCdDocsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
        propertyName: this.propertyData.name,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }

  /**
   * Set current user to inactive
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "properties.detail.deleteTab.confirmDeletion.title"
        ),
        message: this.translateService.instant(
          "properties.detail.deleteTab.confirmDeletion.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.propertiesService
            .delete(this.property.getId())
            .pipe()
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([
                  this.lang,
                  "app",
                  "properties",
                  "suspended",
                ]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Reactivate current user
   *
   * @since 1.0.0
   */
  onReactivate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "properties.detail.deleteTab.confirmActivation.title"
        ),
        message: this.translateService.instant(
          "properties.detail.deleteTab.confirmActivation.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.propertiesService
            .activate(this.property.getId())
            .pipe()
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([this.lang, "app", "properties", "list"]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }
  
  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target?: string, propertyId?: string): void {
    // With the switch live, the two Excels of the property come from the new archive (NEW-M7)
    const archiveV3Target = this.archiveV3Live ? documentArchiveExportTargetOf(target) : null;
    if (archiveV3Target !== null) {
      this.exportFromArchiveV3(archiveV3Target);
      return;
    }

    // If target is not provided, than just export all properties
    if (!target) {
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        "properties.list.exportFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getExport().subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * «Elenco documenti presenti» and «Elenco documenti obbligatori mancanti» with the switch live: the
   * Excel of the new archive, as the «Esporta» menu of its dossier gives it. One download at a time;
   * the entry is checked again here and not only in the template, as the dossier does: a page left
   * open across a role change must not ask for an Excel the new role is not shown.
   *
   * @since 3.0.0
   *
   * @param target Which Excel
   */
  private exportFromArchiveV3(target: DocumentArchiveExportTarget): void {
    if (this.exportingTarget !== null || !this.propertyId || !this.downloadMenu[target]) {
      return;
    }
    this.exportingTarget = target;
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
    this.exportSubscription = this.documentArchiveExportDownloadService
      .download(target, this.propertyId)
      .pipe(
        finalize(() => {
          this.exportingTarget = null;
        })
      )
      .subscribe();
  }
  
  /**
   * RAS docs export in PDF format
   *
   * @since 1.0.0
   */
  onExportRAS(): void {
    // With the switch live, the RAS comes from the new archive, in its own dialog (FE-M8)
    if (this.archiveV3Live) {
      if (this.propertyId && this.downloadMenu.ras) {
        openDocumentArchiveExportRasDialog(this.dialog, this.propertyId);
      }
      return;
    }

    const dialogRef = this.dialog.open(ExportRasDocsDialogComponent, {
      width: "700px",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }
  
  /**
   * Word contracts export
   *
   * @since 1.0.0
   */
  onExportContracts(): void {
    // With the switch live, the contracts come from the new archive, in their own dialog (FE-M8)
    if (this.archiveV3Live) {
      if (this.propertyId && this.downloadMenu.contracts) {
        openDocumentArchiveExportContractsDialog(this.dialog, this.propertyId);
      }
      return;
    }

    const dialogRef = this.dialog.open(ExportContractsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        return;
      });
  }
  
  /**
   * Open the "Elenco documenti Privacy" dialog for this property
   * (Admin/Operatore only, gated in the template via loggedUserAdmin;
   * requires the property's Privacy or Privacy SAG subscription to be active)
   *
   * @since 1.0.0
   */
  onOpenPrivacyDocsDialog(): void {
    if (!this.propertyPrivacyActive) {
      return;
    }

    const companyId = this.propertyData && this.propertyData.admin && this.propertyData.admin.company
      ? this.propertyData.admin.company._id
      : undefined;
    if (!companyId) {
      this.toastrService.error(
        this.translateService.instant("properties.home.privacyDocsDialog.missingCompany")
      );
      return;
    }

    this.dialog.open(PrivacyDocsListDialogComponent, {
      data: {
        propertyId: this.propertyId,
        companyId,
      },
      width: "650px",
      disableClose: true,
    });
  }

  /**
   * Download the "Nomina del responsabile del trattamento dei
   * dati" personalized for this property (Master/Collaboratore only,
   * gated in the template via loggedUserMaster; requires the property's
   * Privacy SAG subscription to be active)
   *
   * @since 1.0.0
   */
  onDownloadDataProcessorAppointment(): void {
    if (this.dataProcessorAppointmentDownloading || !this.propertyPrivacySagActive) {
      return;
    }

    const companyId = this.propertyData && this.propertyData.admin && this.propertyData.admin.company
      ? this.propertyData.admin.company._id
      : undefined;
    if (!companyId) {
      this.toastrService.error(
        this.translateService.instant("properties.home.privacyDocsDialog.missingCompany")
      );
      return;
    }

    this.dataProcessorAppointmentDownloading = true;
    this.subscriptions.push(
      this.privacyDocsService
        .generateDocuments(companyId, [this.propertyId], DATA_PROCESSOR_APPOINTMENT_TEMPLATE)
        .subscribe(
          (fileBlob: Blob) => {
            this.dataProcessorAppointmentDownloading = false;
            const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
            const fileNamePrefix = this.translateService.instant(
              "properties.home.export.dataProcessorAppointment"
            );
            const extension = PRIVACY_DOC_MIME_EXTENSIONS[fileBlob.type] || DEFAULT_PRIVACY_DOC_EXTENSION;
            FileSaver.saveAs(fileBlob, `${fileNamePrefix}-${date}.${extension}`);
          },
          (err) => {
            this.dataProcessorAppointmentDownloading = false;
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Prevent redirect to archive if structure is open
   *
   * @since 1.0.0
   */
   onGoToDocumentArchive(): void {
    if (!this.mappaturaOpen) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.mappaturaOpen.title'),
        message: this.translateService.instant('dialogs.confirm.mappaturaOpen.message'),
        btnCancelText: this.translateService.instant('common.cta.close'),
        messageOnly: true,
      },
      width: '500px',
      disableClose: false,
    });
  }
    
}
